import React from 'react';
import { LayoutDashboard, Users, Settings, Receipt } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../utils/cn';

const NavItem: React.FC<{ to: string; icon: React.ReactNode; label: string; badge?: string; disabled?: boolean }> = ({ to, icon, label, badge, disabled }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  const content = (
    <>
      <div className={cn(
        'rounded-full p-1 relative',
        isActive ? 'bg-primary-50 dark:bg-primary-900/30' : ''
      )}>
        {icon}
        {badge && (
          <span className={cn(
            "absolute -top-1 -right-2 text-[9px] text-white px-1 rounded-full border border-bg-navbar leading-tight z-10",
            disabled ? "bg-amber-500" : "bg-primary-600"
          )}>
            {badge}
          </span>
        )}
      </div>
      <span>{label}</span>
    </>
  );

  if (disabled) {
    return (
      <div className="flex flex-col items-center justify-center space-y-1 text-xs font-medium relative text-text-faint opacity-60 cursor-not-allowed">
        {content}
      </div>
    );
  }

  return (
    <Link
      to={to}
      className={cn(
        'flex flex-col items-center justify-center space-y-1 text-xs font-medium transition-colors relative',
        isActive ? 'text-primary-600' : 'text-text-muted hover:text-text-body'
      )}
    >
      {content}
    </Link>
  );
};

export const Navbar: React.FC = () => {
  const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true' && import.meta.env.DEV;

  return (
    <nav className={cn(
      "fixed bottom-0 left-0 right-0 z-40 border-t border-border-default px-6 h-[var(--navbar-height)] pb-safe-offset-2 transition-colors",
      useEmulators ? "bg-amber-950/30 border-amber-900/50" : "bg-bg-navbar"
    )}>
      <div className="mx-auto flex h-full max-w-md items-center justify-around">
        <NavItem to="/" icon={<Receipt size={24} />} label="Tabs" />
        <NavItem to="/groups" icon={<LayoutDashboard size={24} />} label="Groups" badge="Soon" disabled />
        <NavItem to="/friends" icon={<Users size={24} />} label="Friends" />
        <NavItem to="/settings" icon={<Settings size={24} />} label="Settings" />
      </div>
    </nav>
  );
};
